/**
 * A client for allauth's headless endpoints.
 *
 * These are not in our OpenAPI document — they are allauth's own routes, mounted at
 * `/api/v1/auth/browser/v1/` — so this client is hand-written and hand-typed, unlike `client.ts`
 * which is generated. Keep the types here matching allauth's documented envelope:
 *
 *     { "status": 200, "data": { ... }, "meta": { "is_authenticated": true } }
 *     { "status": 400, "errors": [ { "message": "...", "code": "...", "param": "email" } ] }
 *
 * allauth answers with a 401 both for "not signed in" and for "signed up but not verified yet".
 * The two are told apart by the pending flow in `data.flows`, which is why `AuthError` carries
 * them and the pages branch on `pendingFlow`.
 */

import { csrfHeaders } from "@/api/csrf";

const BASE = "/api/v1/auth/browser/v1";

export interface AllauthErrorDetail {
  message: string;
  code: string;
  param?: string;
}

export interface AllauthFlow {
  id: string;
  is_pending?: boolean;
  providers?: string[];
}

export interface AllauthUser {
  id: number | string;
  email?: string;
  display?: string;
  has_usable_password?: boolean;
}

export interface AllauthProvider {
  id: string;
  name: string;
  /** Empty until the OAuth client exists, which is how we know not to offer the provider. */
  client_id: string;
  flows: string[];
}

export interface AllauthEnvelope {
  status: number;
  data?: {
    user?: AllauthUser;
    flows?: AllauthFlow[];
    account?: { is_open_for_signup?: boolean; login_methods?: string[] };
    socialaccount?: { providers?: AllauthProvider[] };
    [key: string]: unknown;
  };
  errors?: AllauthErrorDetail[];
  meta?: { is_authenticated?: boolean };
}

/**
 * Providers that are actually usable. allauth lists a provider whenever it is installed, even
 * with no credentials, so the presence of a client id is what distinguishes "you can sign in
 * with Google" from "Google is wired but has no OAuth client yet".
 */
export function usableProviders(config: AllauthEnvelope | undefined): AllauthProvider[] {
  return (config?.data?.socialaccount?.providers ?? []).filter((p) => p.client_id !== "");
}

/** A non-2xx answer from allauth, carrying both its field errors and its pending flow. */
export class AuthError extends Error {
  readonly status: number;
  readonly errors: AllauthErrorDetail[];
  readonly flows: AllauthFlow[];

  constructor(envelope: AllauthEnvelope, fallback: string) {
    const errors = envelope.errors ?? [];
    super(errors[0]?.message ?? fallback);
    this.name = "AuthError";
    this.status = envelope.status;
    this.errors = errors;
    this.flows = envelope.data?.flows ?? [];
  }

  /** The step allauth is waiting on, such as `verify_email` after a sign-up. */
  get pendingFlow(): string | undefined {
    return this.flows.find((flow) => flow.is_pending)?.id;
  }

  /** The message for one field, so a form can show it next to the input it belongs to. */
  errorFor(param: string): string | undefined {
    return this.errors.find((error) => error.param === param)?.message;
  }

  /** Messages that belong to no particular field. */
  get formErrors(): string[] {
    return this.errors.filter((error) => !error.param).map((error) => error.message);
  }
}

async function request(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<AllauthEnvelope> {
  const method = init.method ?? "GET";
  const response = await fetch(`${BASE}${path}`, {
    method,
    credentials: "same-origin",
    headers: {
      ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
      ...csrfHeaders(method),
    },
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  });

  // allauth always answers JSON, but a proxy or a crash might not.
  let envelope: AllauthEnvelope;
  try {
    envelope = (await response.json()) as AllauthEnvelope;
  } catch {
    envelope = { status: response.status };
  }
  if (!response.ok) throw new AuthError(envelope, response.statusText || "Request failed");
  return envelope;
}

export const auth = {
  /**
   * Fetch allauth's configuration. Called once when the app starts, for its side effect as much
   * as its body: the response carries Django's CSRF cookie, which every later POST needs.
   */
  config: () => request("/config"),

  /** Current session. Throws `AuthError` with status 401 when there is none. */
  session: () => request("/auth/session"),

  signUp: (email: string, password: string) =>
    request("/auth/signup", { method: "POST", body: { email, password } }),

  signIn: (email: string, password: string) =>
    request("/auth/login", { method: "POST", body: { email, password } }),

  /** allauth answers a successful sign-out with 401: there is no session any more. */
  signOut: () => request("/auth/session", { method: "DELETE" }),

  verifyEmail: (key: string) => request("/auth/email/verify", { method: "POST", body: { key } }),

  resendVerification: (email: string) =>
    request("/auth/email/verify/resend", { method: "POST", body: { email } }),

  requestPasswordReset: (email: string) =>
    request("/auth/password/request", { method: "POST", body: { email } }),

  resetPassword: (key: string, password: string) =>
    request("/auth/password/reset", { method: "POST", body: { key, password } }),
} as const;

/**
 * Where to send the browser to start a social login. This is a full page navigation with a form
 * POST, not fetch: the provider redirects back, which a background request cannot follow.
 */
export function providerRedirect(
  provider: string,
  next = "/",
): {
  url: string;
  fields: Record<string, string>;
} {
  return {
    url: `${BASE}/auth/provider/redirect`,
    fields: { provider, callback_url: next, process: "login" },
  };
}
